// src/components/FarmerAuthModal.jsx
import React, { useState, useEffect } from 'react';

const FarmerAuthModal = ({ onClose, onLoginSuccess, initialMode = 'login' }) => {
  const [isLogin, setIsLogin] = useState(initialMode === 'login');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    verificationToken: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentView, setCurrentView] = useState('auth');
  const [verificationAttempted, setVerificationAttempted] = useState(false);
  const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  // FIXED: Enhanced URL parameter handling with proper cleanup timing
  useEffect(() => {
    if (urlParamsProcessed) return;

    const urlParams = new URLSearchParams(window.location.search);
    const verificationToken = urlParams.get('token');
    const verifyFarmer = urlParams.get('verifyFarmer');
    const resetFarmerPassword = urlParams.get('resetFarmerPassword');
    
    console.log('🔍 FarmerAuthModal URL Parameters:', {
      verificationToken: verificationToken ? `${verificationToken.substring(0, 15)}...` : 'No token',
      verifyFarmer,
      resetFarmerPassword,
      fullURL: window.location.href,
      searchParams: window.location.search
    });

    // Handle email verification
    if (verifyFarmer === 'true' && verificationToken) {
      console.log('🎯 TRIGGERED: Email verification flow in FarmerAuthModal');
      setCurrentView('verify-email');
      setFormData(prev => ({ ...prev, verificationToken }));
      setUrlParamsProcessed(true);
      
      // Auto-trigger verification after modal is fully rendered
      setTimeout(() => {
        handleAutoVerification(verificationToken);
      }, 1000);
    }
    
    // Handle password reset
    if (resetFarmerPassword === 'true' && verificationToken) {
      console.log('🎯 TRIGGERED: Password reset flow in FarmerAuthModal');
      setCurrentView('forgot-password');
      setFormData(prev => ({ ...prev, verificationToken }));
      setUrlParamsProcessed(true);
    }
  }, [urlParamsProcessed]);

  // NEW: Proper URL cleanup function
  const cleanUpUrlParams = () => {
    if (window.history.replaceState) {
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
      console.log('🔄 URL parameters cleaned up - new URL:', newUrl);
    }
  };

  // FIXED: Enhanced auto-verification with proper cleanup
  const handleAutoVerification = async (token) => {
    if (verificationAttempted) {
      console.log('🔄 Verification already attempted, skipping');
      return;
    }
    
    setVerificationAttempted(true);
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      console.log('🔄 Starting auto-verification with token:', token ? `${token.substring(0, 15)}...` : 'No token');
      
      const response = await fetch(`${API_URL}/api/farmers/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();
      console.log('📨 Verification response:', data);

      if (!response.ok) {
        if (data.message?.includes('already verified')) {
          setSuccess('Email already verified! You can now log in.');
          cleanUpUrlParams();
        } else if (data.message?.includes('expired')) {
          setError('Verification link has expired. Please request a new verification email.');
          cleanUpUrlParams();
        } else {
          throw new Error(data.message || 'Verification failed');
        }
      } else {
        setSuccess('Email verified successfully! You can now log in.');
        cleanUpUrlParams();
      }

    } catch (error) {
      console.error('❌ Verification error:', error);
      
      if (error.message.includes('Failed to fetch')) {
        setError('Cannot connect to server. Please check your internet connection and try again.');
      } else if (error.message.includes('expired')) {
        setError('Verification link has expired. Please request a new verification email.');
        cleanUpUrlParams();
      } else if (error.message.includes('already verified')) {
        setSuccess('Email is already verified! You can now log in.');
        cleanUpUrlParams();
      } else {
        setError(error.message || 'Verification failed. Please try again or request a new verification email.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  const validateForm = () => {
    if (isLogin) {
      if (!formData.email || !formData.password) {
        setError('Please fill in all fields');
        return false;
      }
      if (!validateEmail(formData.email)) {
        setError('Please enter a valid email address');
        return false;
      }
    } else {
      if (!formData.name || !formData.email || !formData.phone || !formData.password || !formData.confirmPassword) {
        setError('Please fill in all fields');
        return false;
      }
      if (!validateEmail(formData.email)) {
        setError('Please enter a valid email address');
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters long');
        return false;
      }
      // Enhanced phone validation
      if (!/^\+?[\d\s\-\(\)]{10,}$/.test(formData.phone)) {
        setError('Please enter a valid phone number with country code (e.g., +254...)');
        return false;
      }
    }
    return true;
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      console.log('🔐 Attempting login for:', formData.email);
      
      const response = await fetch(`${API_URL}/api/farmers/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email.toLowerCase(),
          password: formData.password
        }),
      });

      const data = await response.json();
      console.log('📨 Login response:', data);

      if (!response.ok) {
        if (data.requiresVerification) {
          throw new Error('Please verify your email before logging in. Check your inbox or request a new verification email.');
        }
        if (data.lockout) {
          throw new Error(`Account temporarily locked. Please try again in ${data.lockoutTime} minutes.`);
        }
        throw new Error(data.message || 'Login failed');
      }

      console.log('✅ Login successful, storing auth data');
      
      // Store auth data and close modal
      const authState = {
        user: data.user,
        token: data.token,
        userType: 'farmer',
        isAuthenticated: true
      };
      
      localStorage.setItem('auth', JSON.stringify(authState));
      onLoginSuccess(authState);
      handleClose();

    } catch (error) {
      console.error('❌ Login error:', error);
      setError(error.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/farmers/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email.toLowerCase(),
          phone: formData.phone,
          password: formData.password
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }

      if (data.requiresVerification) {
        setSuccess('Registration successful! Please check your email to verify your account before logging in.');
        setIsLogin(true);
        setFormData(prev => ({ 
          ...prev, 
          name: '',
          phone: '',
          password: '', 
          confirmPassword: '' 
        }));
      } else {
        setSuccess('Registration successful! You can now log in with your credentials.');
        setIsLogin(true);
        setFormData(prev => ({ 
          ...prev, 
          name: '',
          phone: '',
          password: '', 
          confirmPassword: '' 
        }));
      }

    } catch (error) {
      console.error('Registration error:', error);
      setError(error.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/farmers/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email.toLowerCase()
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send reset email');
      }

      setSuccess('If an account exists with this email, you will receive a password reset link shortly.');
      setFormData(prev => ({ ...prev, email: '' }));

    } catch (error) {
      console.error('Forgot password error:', error);
      setError(error.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/farmers/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email.toLowerCase()
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send verification email');
      }

      setSuccess('Verification email sent successfully! Please check your inbox.');

    } catch (error) {
      console.error('Resend verification error:', error);
      setError(error.message || 'Failed to send verification email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    if (currentView === 'forgot-password') {
      handleForgotPassword(e);
    } else if (isLogin) {
      handleLogin(e);
    } else {
      handleRegister(e);
    }
  };

  const switchMode = () => {
    setIsLogin(!isLogin);
    setError('');
    setSuccess('');
    setFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: ''
    });
  };

  const showForgotPassword = () => {
    setCurrentView('forgot-password');
    setError('');
    setSuccess('');
  };

  const showAuth = () => {
    setCurrentView('auth');
    setError('');
    setSuccess('');
  };

  // FIXED: Handle modal close with cleanup
  const handleClose = () => {
    // Clean up URL params when modal closes if they were processed
    if (urlParamsProcessed) {
      cleanUpUrlParams();
    }
    onClose();
  };

  const renderForgotPassword = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-xl font-bold text-gray-800">Reset Your Password</h3>
        <p className="text-gray-600 text-sm mt-2">
          Enter your email address and we'll send you a link to reset your password.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Email Address *
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          placeholder="your.email@example.com"
          disabled={loading}
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {loading ? (
          <>
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Sending Reset Link...
          </>
        ) : (
          'Send Reset Link'
        )}
      </button>

      <div className="text-center">
        <button
          type="button"
          onClick={showAuth}
          className="text-green-600 font-semibold hover:text-green-700 text-sm"
          disabled={loading}
        >
          ← Back to Login
        </button>
      </div>
    </div>
  );

  const renderVerification = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-xl font-bold text-gray-800">
          {loading ? 'Verifying Your Email' : 'Email Verification'}
        </h3>
        <p className="text-gray-600 text-sm mt-2">
          {loading 
            ? 'Please wait while we verify your email address...' 
            : success 
              ? 'Email verification complete! You can now log in.'
              : error || 'Processing your verification request...'
          }
        </p>
      </div>

      {loading && (
        <div className="flex justify-center">
          <svg className="animate-spin h-8 w-8 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      )}

      {error && !loading && (
        <div className="space-y-3">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm text-center">
              {error}
            </p>
          </div>
          
          {/* Show resend option for specific errors */}
          {(error.includes('expired') || error.includes('Invalid') || error.includes('failed')) && (
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2">Need a new verification email?</p>
              <button
                onClick={() => {
                  setCurrentView('auth');
                  setError('');
                }}
                className="text-green-600 font-semibold hover:text-green-700 text-sm underline"
              >
                Request New Verification Email
              </button>
            </div>
          )}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-800 text-sm text-center">
            ✅ {success}
          </p>
          <div className="text-center mt-3">
            <button
              onClick={() => {
                setCurrentView('auth');
                setIsLogin(true);
              }}
              className="text-green-600 font-semibold hover:text-green-700 text-sm underline"
            >
              Proceed to Login
            </button>
          </div>
        </div>
      )}

      <div className="text-center">
        <button
          type="button"
          onClick={showAuth}
          className="text-green-600 font-semibold hover:text-green-700 text-sm"
          disabled={loading}
        >
          ← Back to Login
        </button>
      </div>
    </div>
  );

  const renderAuthForm = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Name Field - Only for Registration */}
      {!isLogin && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Full Name *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="Enter your full name"
            disabled={loading}
          />
        </div>
      )}

      {/* Email Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Email Address *
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          placeholder="your.email@example.com"
          disabled={loading}
        />
      </div>

      {/* Phone Field - Only for Registration */}
      {!isLogin && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number *
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="+254 XXX XXX XXX"
            disabled={loading}
          />
          <p className="text-xs text-gray-500 mt-1">Include country code (e.g., +254 for Kenya)</p>
        </div>
      )}

      {/* Password Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Password *
        </label>
        <input
          type="password"
          name="password"
          value={formData.password}
          onChange={handleInputChange}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          placeholder={isLogin ? "Enter your password" : "Create a password (min. 6 characters)"}
          disabled={loading}
        />
      </div>

      {/* Confirm Password - Only for Registration */}
      {!isLogin && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Confirm Password *
          </label>
          <input
            type="password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="Confirm your password"
            disabled={loading}
          />
        </div>
      )}

      {/* Forgot Password Link - Only for Login */}
      {isLogin && (
        <div className="text-right">
          <button
            type="button"
            onClick={showForgotPassword}
            className="text-green-600 font-semibold hover:text-green-700 text-sm"
            disabled={loading}
          >
            Forgot your password?
          </button>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {loading ? (
          <>
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {isLogin ? 'Signing In...' : 'Creating Account...'}
          </>
        ) : (
          isLogin ? 'Sign In' : 'Create Account'
        )}
      </button>
    </form>
  );

  const getCurrentContent = () => {
    switch (currentView) {
      case 'forgot-password':
        return renderForgotPassword();
      case 'verify-email':
        return renderVerification();
      default:
        return renderAuthForm();
    }
  };

  const getHeaderTitle = () => {
    switch (currentView) {
      case 'forgot-password':
        return 'Reset Your Password';
      case 'verify-email':
        return 'Email Verification';
      default:
        return isLogin ? 'Welcome Back' : 'Join Our Farming Community';
    }
  };

  const getHeaderSubtitle = () => {
    switch (currentView) {
      case 'forgot-password':
        return "We'll help you get back into your account";
      case 'verify-email':
        return 'Verifying your email address';
      default:
        return isLogin 
          ? 'Sign in to request farm inputs and help reduce waste' 
          : 'Create an account to access affordable farm inputs';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
        {/* Debug info - remove in production */}
        {process.env.NODE_ENV === 'development' && (
          <div className="text-xs text-gray-500 mb-2 p-2 bg-gray-100 rounded">
            URL Processed: {urlParamsProcessed ? 'Yes' : 'No'} | 
            View: {currentView} | 
            Verification Attempted: {verificationAttempted ? 'Yes' : 'No'}
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            {getHeaderTitle()}
          </h2>
          <p className="text-gray-600 text-sm mt-2">
            {getHeaderSubtitle()}
          </p>
        </div>

        {/* Environmental Impact Banner - Only show for auth views */}
        {currentView === 'auth' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6">
            <p className="text-green-800 text-sm text-center">
              <strong>🌱 Help Reduce Waste:</strong> Get quality inputs at great prices while supporting sustainable farming
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <p className="text-green-800 text-sm">{success}</p>
          </div>
        )}

        {/* Form */}
        {getCurrentContent()}

        {/* Switch Mode - Only show for auth view */}
        {currentView === 'auth' && (
          <div className="text-center mt-4">
            <p className="text-gray-600 text-sm">
              {isLogin ? "Don't have an account? " : "Already have an account? "}
              <button
                type="button"
                onClick={switchMode}
                className="text-green-600 font-semibold hover:text-green-700 focus:outline-none"
                disabled={loading}
              >
                {isLogin ? 'Sign Up' : 'Sign In'}
              </button>
            </p>
          </div>
        )}

        {/* Close Button - use handleClose instead of onClose */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl"
          disabled={loading}
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default FarmerAuthModal;